import React, {useState} from 'react';
import {
    Create,
    Edit,
    SimpleForm,
    useRecordContext,
    usePermissions,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {Avatar, Box, Typography} from '@mui/material';
import {DeleteOutline} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useUmbrellaUuidChoices} from '../lib/customHooks';
import hasPermission from '../auth/hasPermission';

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const EmailLockToggle = ({isEditable, onToggle, translate}) => (
    <Box
        onClick={onToggle}
        role="button"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
            cursor: 'pointer',
            fontSize: `${t.type.caption.size}px`,
            color: isEditable ? t.color.status.warningSolid : t.color.text.secondary,
            userSelect: 'none',
        }}
    >
        <input type="checkbox" checked={isEditable} readOnly style={{margin: 0}}/>
        {translate('Make editable')}
    </Box>
);

const HeaderBar = ({mode, record, translate, canDelete, onDelete}) => {
    const fullName = record
        ? [record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed'
        : null;

    return (
        <PageHeader
            breadcrumb={{label: translate('Umbrella Admins'), to: '/umbrellaAdmins'}}
            title={
                mode === 'create' ? (
                    <Box component="span">{translate('Create umbrella admin')}</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record?.enabled ? 'success' : 'neutral'}/>
                        <Box component="span">{fullName}</Box>
                    </Box>
                )
            }
            actions={
                <>
                    <SaveAction label={mode === 'create' ? translate('Create umbrella admin') : translate('Save changes')}/>
                    {mode === 'edit' && canDelete && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const deleteConfirm = useDeleteConfirm();

    const umbrellaChoices = useUmbrellaUuidChoices();
    const canDelete = hasPermission(permissions, ['delete'], 'umbrellaAdmins');
    const canPickUmbrella = permissions === 'admin';

    const [isEmailEditable, setEmailEditable] = useState(mode === 'create');

    if (mode === 'edit' && !record) return null;
    if (!umbrellaChoices) return null;

    const fullName = record
        ? [record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed'
        : null;

    const avatarUrl = record?.avatar ? `${process.env.REACT_APP_CDN_URL}/${record.avatar}` : undefined;

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                canDelete={canDelete}
                onDelete={deleteConfirm.openDialog}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="umbrellaAdmins"
                    record={record}
                    recordLabel={fullName}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('Basic Information')}>
                        {mode === 'edit' && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2, pb: 2, borderBottom: `1px solid ${t.color.border.hairline}`}}>
                                <Avatar
                                    src={avatarUrl}
                                    sx={{
                                        width: 48, height: 48,
                                        bgcolor: t.color.sidebarHover,
                                        color: t.color.text.secondary,
                                        border: `1px solid ${t.color.border.hairline}`,
                                    }}
                                >
                                    {(record?.given_name?.charAt(0) ?? '') + (record?.family_name?.charAt(0) ?? '')}
                                </Avatar>
                                <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 600, color: t.color.text.primary}}>
                                    {fullName}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="given_name" label={translate('First name')} required/>
                            <StatamicTextInput source="family_name" label={translate('Last name')} required/>
                        </Box>
                        <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start'}}>
                            <Box>
                                <StatamicTextInput
                                    source="email"
                                    label={translate('email')}
                                    type="email"
                                    required
                                    helperText={mode === 'edit' ? translate('Edit email helper text') : undefined}
                                    key={isEmailEditable ? 'editable' : 'locked'}
                                />
                                {mode === 'edit' && (
                                    <EmailLockToggle
                                        isEditable={isEmailEditable}
                                        onToggle={() => setEmailEditable(v => !v)}
                                        translate={translate}
                                    />
                                )}
                            </Box>
                            {mode === 'create' && (
                                <StatamicTextInput
                                    source="password"
                                    label={translate('Tmp password')}
                                    required
                                    defaultValue="TempWw@Nf"
                                />
                            )}
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Umbrella')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicSelectInput
                                source="umbrella_external_id"
                                label={translate('Umbrella')}
                                required
                                choices={umbrellaChoices || []}
                                disabled={!canPickUmbrella}
                                defaultValue={record?.umbrella?.external_id}
                            />
                        </Box>
                    </SectionCard>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title={translate('Status')}>
                        <ToggleRow
                            source="enabled"
                            label={translate('Enabled')}
                            subtitle={translate('Enabled helper text')}
                            defaultValue={mode === 'create'}
                        />
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

export const UmbrellaAdminEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const UmbrellaAdminCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default UmbrellaAdminEditStatamic;
